import { useState, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileDown,
  Send,
  Copy,
  CheckCircle,
  PenLine,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useIndustry } from '@/hooks/use-industry';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Alert,
  AlertDescription,
} from '@/app/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/components/ui/alert-dialog';
import {
  useBid,
  useSendBid,
  useAcceptBid,
  useDuplicateBid,
  useDownloadBidPdf,
  useBidAvailability,
} from '@/hooks/use-projects';
import type { BidStatus, BidLineType } from '@/lib/types';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';

const statusColors: Record<BidStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<BidStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  under_review: 'Under Review',
  accepted: 'Accepted',
  rejected: 'Rejected',
  expired: 'Expired',
};

const lineTypeLabels: Record<BidLineType, string> = {
  labor: 'Labor',
  equipment: 'Equipment',
  materials: 'Materials',
  travel: 'Travel',
  per_diem: 'Per Diem',
  subcontractor: 'Subcontractor',
  other: 'Other',
};

export function BidDetailPage() {
  const currency = useOrgCurrency();
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const bidId = parseInt(id || '0', 10);
  const { data: bid, isLoading } = useBid(bidId);
  const { labels } = useIndustry();

  const sendMutation = useSendBid();
  const acceptMutation = useAcceptBid();
  const duplicateMutation = useDuplicateBid();
  const downloadPdfMutation = useDownloadBidPdf();
  const { data: availability } = useBidAvailability(bidId);

  const [actionError, setActionError] = useState<string | null>(null);

  const isDraft = bid?.status === 'draft';
  const canSend = isDraft;
  const canAccept = bid?.status === 'sent' || bid?.status === 'under_review';

  const handleSend = () => {
    if (!bid) return;
    setActionError(null);
    sendMutation.mutate(bid.id, {
      onError: () => setActionError('Failed to send bid.'),
    });
  };

  const handleAccept = () => {
    if (!bid) return;
    setActionError(null);
    acceptMutation.mutate(bid.id, {
      onError: () => setActionError('Failed to accept bid.'),
    });
  };

  const handleDuplicate = () => {
    if (!bid) return;
    setActionError(null);
    duplicateMutation.mutate(bid.id, {
      onSuccess: (newBid) => navigate(`/projects/bids/${newBid.id}`),
      onError: () => setActionError('Failed to duplicate bid.'),
    });
  };

  const handleDownloadPdf = () => {
    if (!bid) return;
    downloadPdfMutation.mutate(bid.id);
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!bid) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Bid not found.</p>
        <Button asChild className="mt-4">
          <Link to="/projects/bids">Back to Bids</Link>
        </Button>
      </div>
    );
  }

  // Group line items by section
  const sections = bid.sections || [];
  const lineItems = bid.line_items || [];
  const unsectionedItems = lineItems.filter((li) => li.section === null);
  const gearLines = bid.bid_gear_lines || [];

  const availabilityMap = new Map(
    (availability || []).map((row) => [row.id, row])
  );

  const lineSubtotal = parseFloat(bid.subtotal) - gearLines.reduce(
    (sum, line) => sum + parseFloat(line.total || '0'),
    0
  );
  const gearSubtotal = gearLines.reduce(
    (sum, line) => sum + parseFloat(line.total || '0'),
    0
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects/bids')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{bid.title}</h1>
              <Badge className={statusColors[bid.status]}>
                {statusLabels[bid.status]}
              </Badge>
            </div>
            <p className="text-gray-600">
              {bid.bid_number} &middot; {bid.client_name}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={downloadPdfMutation.isPending}
          >
            <FileDown className="h-4 w-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleDuplicate} disabled={duplicateMutation.isPending}>
            <Copy className="h-4 w-4 mr-1" />
            Duplicate
          </Button>
          {isDraft && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/projects/bids/${bid.id}/edit`}>
                <PenLine className="h-4 w-4 mr-1" />
                Edit
              </Link>
            </Button>
          )}
          {canSend && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm">
                  <Send className="h-4 w-4 mr-1" />
                  Send to {labels.client}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Send Bid to {labels.client}</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will mark the bid as sent and record the timestamp.
                    The bid will no longer be editable.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleSend} disabled={sendMutation.isPending}>
                    {sendMutation.isPending ? 'Sending...' : 'Send Bid'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {canAccept && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Accept Bid
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Accept Bid</AlertDialogTitle>
                  <AlertDialogDescription>
                    Accepting this bid will create a new Project with the bid details.
                    The project will be set to &quot;awarded&quot; status with a budget of{' '}
                    {formatMoney(bid.total, currency)}.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleAccept} disabled={acceptMutation.isPending}>
                    {acceptMutation.isPending ? 'Accepting...' : 'Accept & Create Project'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {actionError && (
        <Alert variant="destructive">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {bid.status === 'accepted' && bid.project && (
        <Alert>
          <FileText className="h-4 w-4" />
          <AlertDescription>
            This bid has been accepted and linked to a project.
          </AlertDescription>
        </Alert>
      )}

      {/* Bid Info */}
      <Card>
        <CardHeader>
          <CardTitle>Bid Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 block">Bid Number</span>
              <span className="font-medium">{bid.bid_number}</span>
            </div>
            <div>
              <span className="text-gray-500 block">{labels.client}</span>
              <span className="font-medium">{bid.client_name}</span>
            </div>
            <div>
              <span className="text-gray-500 block">Created</span>
              <span className="font-medium">
                {formatDateTimeInTimeZone(bid.created_at, timeZone, { dateStyle: 'medium' })}
              </span>
            </div>
            <div>
              <span className="text-gray-500 block">Valid Until</span>
              <span className="font-medium">
                {bid.valid_until
                  ? formatDateOnly(bid.valid_until)
                  : 'Not set'}
              </span>
            </div>
            {bid.sent_at && (
              <div>
                <span className="text-gray-500 block">Sent</span>
                <span className="font-medium">
                  {formatDateTimeInTimeZone(bid.sent_at, timeZone, { dateStyle: 'medium' })}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-500 block">Created By</span>
              <span className="font-medium">{bid.created_by_username}</span>
            </div>
          </div>
          {bid.description && (
            <div className="mt-4">
              <span className="text-sm text-gray-500 block mb-1">Description</span>
              <p className="text-sm whitespace-pre-wrap">{bid.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sections.map((section) => {
                const sectionItems = lineItems.filter((li) => li.section === section.id);
                if (sectionItems.length === 0) return null;
                return (
                  <SectionRows
                    key={section.id}
                    sectionTitle={section.title}
                    items={sectionItems}
                  />
                );
              })}
              {unsectionedItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="text-gray-600">
                    {lineTypeLabels[item.line_type]}
                  </TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-gray-600">{item.unit}</TableCell>
                  <TableCell className="text-right">{formatMoney(item.unit_price, currency)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMoney(item.total, currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Gear Estimate */}
      {gearLines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Gear Estimate</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gearLines.map((line) => {
                  const row = line.id ? availabilityMap.get(line.id) : undefined;
                  return (
                    <Fragment key={line.id ?? line.description}>
                      <TableRow>
                        <TableCell>{line.description}</TableCell>
                        <TableCell className="text-gray-600">
                          {formatMoney(line.override_rate ?? line.base_rate, currency)}{' '}
                          {line.rate_unit.replace('_', ' ')}
                          {line.override_rate && (
                            <span className="text-xs text-blue-600 ml-1">(override)</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{line.quantity}</TableCell>
                        <TableCell className="text-gray-600">
                          {line.start_date && line.end_date
                            ? `${formatDateOnly(line.start_date)} – ${formatDateOnly(line.end_date)}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatMoney(line.total ?? '0', currency)}
                        </TableCell>
                      </TableRow>
                      {line.adjustments && line.adjustments.length > 0 && (
                        <TableRow className="bg-gray-50">
                          <TableCell colSpan={5}>
                            <span className="text-xs text-gray-500">Adjustments:</span>
                            <ul className="text-xs text-gray-600 list-disc ml-4">
                              {line.adjustments.map((adj) => (
                                <li key={adj.id || `${line.id ?? 'new'}-${adj.description}`}>
                                  {adj.adjustment_type === 'discount' ? 'Discount' : 'Allowance'}:{' '}
                                  {adj.description || '—'} ({formatMoney(adj.total ?? '0', currency)})
                                </li>
                              ))}
                            </ul>
                          </TableCell>
                        </TableRow>
                      )}
                      {row?.is_scarce_warning && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 text-sm">
                              <AlertTriangle className="h-4 w-4" />
                              <span>
                                Scarcity advisory: {row.committed} of {row.total_owned} units
                                committed ({row.available} available). This is advisory only and
                                does not block the bid.
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Totals */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-end gap-2 text-sm">
            {lineItems.length > 0 && (
              <div className="flex gap-8">
                <span className="text-gray-600">Line Items Subtotal:</span>
                <span className="font-medium w-32 text-right">{formatMoney(lineSubtotal, currency)}</span>
              </div>
            )}
            {gearLines.length > 0 && (
              <div className="flex gap-8">
                <span className="text-gray-600">Gear Subtotal:</span>
                <span className="font-medium w-32 text-right">{formatMoney(gearSubtotal, currency)}</span>
              </div>
            )}
            <div className="flex gap-8">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium w-32 text-right">{formatMoney(bid.subtotal, currency)}</span>
            </div>
            {parseFloat(bid.tax_rate) > 0 && (
              <div className="flex gap-8">
                <span className="text-gray-600">Tax ({bid.tax_rate}%):</span>
                <span className="font-medium w-32 text-right">
                  {formatMoney(bid.tax_amount, currency)}
                </span>
              </div>
            )}
            <div className="flex gap-8 text-lg font-bold border-t pt-2 mt-1">
              <span>Total:</span>
              <span className="w-32 text-right">{formatMoney(bid.total, currency)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Terms & Conditions */}
      {bid.terms_and_conditions && (
        <Card>
          <CardHeader>
            <CardTitle>Terms & Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {bid.terms_and_conditions}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {bid.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{bid.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Helper component for section rows
function SectionRows({
  sectionTitle,
  items,
}: {
  sectionTitle: string;
  items: Array<{
    id: number;
    line_type: BidLineType;
    description: string;
    quantity: string;
    unit: string;
    unit_price: string;
    total: string;
  }>;
}) {
  const currency = useOrgCurrency();

  return (
    <>
      <TableRow className="bg-gray-50">
        <TableCell colSpan={6} className="font-medium text-gray-900">
          {sectionTitle}
        </TableCell>
      </TableRow>
      {items.map((item) => (
        <TableRow key={item.id}>
          <TableCell className="text-gray-600 pl-6">
            {lineTypeLabels[item.line_type]}
          </TableCell>
          <TableCell>{item.description}</TableCell>
          <TableCell className="text-right">{item.quantity}</TableCell>
          <TableCell className="text-gray-600">{item.unit}</TableCell>
          <TableCell className="text-right">{formatMoney(item.unit_price, currency)}</TableCell>
          <TableCell className="text-right font-medium">{formatMoney(item.total, currency)}</TableCell>
        </TableRow>
      ))}
    </>
  );
}
